import _, {
  difference,
  get,
  isEmpty,
  uniq,
  last,
  orderBy,
  isNil,
  without,
} from 'lodash';
import {
  CLAIM_STATUS,
  MISSION_POSTPONE_STATUS,
  PROCEDURE_STATUSES,
  PROCEDURE_STEPS,
} from 'containers/data-structure';
import messages from 'containers/LawyerLegalProceedingsPage/messages';
import { FEATURES } from 'features/features';
import { hasFeature } from 'features/fflip-helpers';
import {
  ICurrentSteps,
  IJudgementAdd,
  ILabel,
  IntlMessage,
} from 'containers/LawyerLegalProceedingsPage/typings';
import currentIntervenantStorage from 'containers/services/security/current-intervenant-storage';
import { JudgementADDDocumentType } from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import {
  ListProceeding,
  INJURY_POST_KEY,
  PROCEDURE_TYPES,
  TRANSACTION_STATE,
} from 'containers/LawyerLegalProceedingsPage/constants';
import * as featureService from 'features/fflip-helpers';
import {
  STATUS_APPEL,
  STATUS_CONTESTED,
  STATUS_PENDING,
  STATUS_REJECTED,
  STATUS_VALIDATED,
  STATUS_VALIDATED_WITHOUT_CONTESTATION,
} from 'containers/LawyerFeeNote/constants';
import {
  JUDICIARY_PROCESS_CORRECTIONAL_ID,
  JUDICIARY_PROCESS_COMMERCIALE_ID,
  JUDICIARY_PROCESS_CIVILE_ID,
} from 'containers/data-structure';
import { translate } from 'containers/services/translate';
import { getBeneficiaries } from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/hooks';
import { JUDICIARY_COLOR_STATUS } from 'containers/CourtManagerLegalProceedings/constants';
import { getCivilJudgement } from 'containers/CourtManagerLegalProceedings/JudgementOnMerits/utils';
import { removeWhiteSpaceFromString } from 'services/utils';
import { formatLabel } from 'helpers/utils';
import { ClaimRecapType } from 'typings/claimTypes';
import {
  BeneficiaryType,
  InstructionType,
  TransactionType,
} from 'typings/globalTypes';

export type fflipTypes = 'expertReport';

export const findStatusByStep = (
  step: string,
  currentSteps: ICurrentSteps[],
): string => {
  return currentSteps.find(s => s.step === step)?.status || '';
};

export const shouldAddLabelForLawyer = (key: string, status: string): boolean =>
  hasFeature(key) &&
  (status === PROCEDURE_STATUSES.VALIDATED ||
    status === PROCEDURE_STATUSES.APPEL ||
    status === PROCEDURE_STATUSES.MODIFIED ||
    status === PROCEDURE_STATUSES.VALIDATED_WITHOUT_CONTESTATION);

export const shouldAddLabelForManager = (
  key: string,
  status: string,
): boolean => hasFeature(key) && !!status;

export const labelHelpers = {
  shouldAddLabelForLawyer,
  shouldAddLabelForManager,
  findStatusByStep,
};
export const getLabelByFFlip = (
  claimSteps: ICurrentSteps[],
  isManager?: boolean,
): IntlMessage[] => {
  const labels: ILabel[] = [
    {
      [FEATURES.LAWYER_JUDGEMENT_ADD ||
      FEATURES.MANAGER_JUDGEMENT_ADD]: messages.judgementAddTabTitle,
      status: labelHelpers.findStatusByStep(
        PROCEDURE_STEPS.JUDGEMENT_ADD,
        claimSteps,
      ),
      first: true,
    },
    {
      [FEATURES.EXPERT_REPORT]: messages.expertReportTabTitle,
      status: labelHelpers.findStatusByStep(
        PROCEDURE_STEPS.EXPERT_REPORT,
        claimSteps,
      ),
    },
    {
      [FEATURES.JUDGEMENT_ON_MERITS]: messages.judgementOnMeritsTabTitle,
      status: labelHelpers.findStatusByStep(
        PROCEDURE_STEPS.JUDGEMENT_ON_MERITS,
        claimSteps,
      ),
    },
    {
      [FEATURES.JUDGEMENT_APPEAL]: messages.JudgementAppealTabTitle,
      status: labelHelpers.findStatusByStep(
        PROCEDURE_STEPS.JUDGEMENT_APPEAL,
        claimSteps,
      ),
    },
  ];
  const formattedMessages: IntlMessage[] = [];
  let isFirstCall = true;

  if (!isManager) {
    labels.reduce((previousLabel, currentLabel) => {
      //adding first step for lawyer
      if (previousLabel.first && isFirstCall) {
        const label = (Object.values(
          previousLabel,
        )[0] as unknown) as IntlMessage;
        formattedMessages.push(label);
        isFirstCall = false;
      }
      Object.keys(currentLabel).map(key => {
        if (labelHelpers.shouldAddLabelForLawyer(key, previousLabel.status)) {
          const message = currentLabel[key] as IntlMessage;
          formattedMessages.push(message);
        }
        return formattedMessages;
      });
      return previousLabel;
    });
  } else {
    labels.slice(0, claimSteps.length).forEach(label => {
      Object.keys(label).map(key => {
        //if (labelHelpers.shouldAddLabelForManager(key, label.status)) {
        formattedMessages.push(label[key] as IntlMessage);
        //}
        return formattedMessages;
      });
    });
  }
  return formattedMessages.filter(el => typeof el === 'object');
};
export const renderLengthTabs = (currentSteps: ICurrentSteps[]) => {
  if (
    currentSteps.length &&
    (currentSteps[currentSteps.length - 1].status ===
      PROCEDURE_STATUSES.VALIDATED ||
      currentSteps[currentSteps.length - 1].status ===
        PROCEDURE_STATUSES.APPEL ||
      currentSteps[currentSteps.length - 1].status ===
        PROCEDURE_STATUSES.MODIFIED ||
      currentSteps[currentSteps.length - 1].status ===
        PROCEDURE_STATUSES.VALIDATED_WITHOUT_CONTESTATION)
  ) {
    return currentSteps.length + 1;
  } else if (currentSteps.length === 0) {
    return 0;
  } else {
    return currentSteps.length;
  }
};

export const isJudiciaryProcessLawyerCode = (
  civilProceeding: any,
  sagis_code: string,
) => {
  return (
    civilProceeding &&
    sagis_code &&
    civilProceeding.map((el: any) => el.assignedLawyer).includes(sagis_code)
  );
};
export const getJudgementDate = (
  judgementAddDocuments: JudgementADDDocumentType[],
  documentType: string,
) => {
  const judgementADD = judgementAddDocuments.find(
    judgementAddDocument => judgementAddDocument.type === documentType,
  );

  return get(judgementADD, 'judgementDate', undefined);
};

export const getLastJudgementDate = (
  judgementAddDocuments: JudgementADDDocumentType[],
) => {
  return get(
    last(
      judgementAddDocuments &&
        judgementAddDocuments.sort(
          (objA: any, objB: any) =>
            Number(new Date(objA?.judgementDate)) -
            Number(new Date(objB?.judgementDate)),
        ),
    ),
    'judgementDate',
  );
};

export const getJudiciaryLabelColor = (status: string) => {
  switch (status) {
    case STATUS_CONTESTED:
    case STATUS_VALIDATED:
    case STATUS_VALIDATED_WITHOUT_CONTESTATION:
    case STATUS_APPEL:
    case PROCEDURE_STATUSES.MODIFIED:
    case PROCEDURE_STATUSES.OPPOSITION:
      return JUDICIARY_COLOR_STATUS.VALIDATED;
    case STATUS_PENDING:
      return JUDICIARY_COLOR_STATUS.PENDING;
    case STATUS_REJECTED:
    case PROCEDURE_STATUSES.TO_EDIT:
      return JUDICIARY_COLOR_STATUS.REJECTED;
  }
};

export const getSteps = (
  type: string,
  isExpertReportDefined?: boolean,
  isExpertExpertiseDefined?: boolean,
) => {
  if (type === PROCEDURE_TYPES.APPEAL) {
    return [
      messages.appealAddTitle,
      messages.appealExpertReportTitle,
      ...(isExpertExpertiseDefined
        ? [messages.expertReportConsultTabTitle]
        : []),
      messages.appealOnMeritsTitle,
    ];
  }
  return [
    messages.judgementAddTabTitle,
    ...(isExpertReportDefined ? [messages.expertReportTabTitle] : []),
    ...(isExpertExpertiseDefined ? [messages.expertReportConsultTabTitle] : []),
    messages.judgementOnMeritsLabelTitle,
  ];
};
export const getStepsWithColor = (
  type: string,
  isExpertReportDefined: boolean,
  isExpertExpertiseDefined: boolean,
  judgementAdd: any,
  expertReport: any,
  judgementOnMerits: any,
  expertExpertise: any,
) => {
  const STEP_CONFIG = {
    [PROCEDURE_TYPES.APPEAL]: [
      {
        message: messages.appealAddTitle,
        color: getStepStatusColor(
          'judgementAdd',
          get(judgementAdd, 'status'),
          expertReport,
          judgementAdd,
          judgementOnMerits,
        ),
      },
      {
        message: messages.appealExpertReportTitle,
        color: getStepStatusColor(
          'expertReport',
          get(expertReport, 'status'),
          expertReport,
          judgementAdd,
          judgementOnMerits,
        ),
      },
      ...(isExpertExpertiseDefined
        ? [
            {
              message: messages.expertReportConsultTabTitle,
              color: getExpertiseStatusColor(expertExpertise),
            },
          ]
        : []),
      {
        message: messages.appealOnMeritsTitle,
        color: getStepStatusColor(
          'judgementOnMerits',
          get(judgementOnMerits, 'judgementOnMerits.status'),
          expertReport,
          judgementAdd,
          judgementOnMerits,
        ),
      },
    ],
    DEFAULT: [
      {
        message: messages.judgementAddTabTitle,
        color: getStepStatusColor(
          'judgementAdd',
          get(judgementAdd, 'status'),
          expertReport,
          judgementAdd,
          judgementOnMerits,
        ),
      },
      ...(isExpertReportDefined
        ? [
            {
              message: messages.expertReportTabTitle,
              color: getStepStatusColor(
                'expertReport',
                get(expertReport, 'status'),
                expertReport,
                judgementAdd,
                judgementOnMerits,
              ),
            },
          ]
        : []),
      ...(isExpertExpertiseDefined
        ? [
            {
              message: messages.expertReportConsultTabTitle,
              color: getExpertiseStatusColor(expertExpertise),
            },
          ]
        : []),
      {
        message: messages.judgementOnMeritsLabelTitle,
        color: getStepStatusColor(
          'judgementOnMerits',
          get(judgementOnMerits, 'judgementOnMerits.status'),
          expertReport,
          judgementAdd,
          judgementOnMerits,
        ),
      },
    ],
  };
  return STEP_CONFIG[type] || STEP_CONFIG.DEFAULT;
};
export const getStepStatusColor = (
  stepType: string,
  status: string,
  expertReport: any,
  judgementAdd: any,
  judgementOnMerits: any,
) => {
  if (stepType === 'judgementAdd' && !isEmpty(judgementAdd)) {
    const hasPending =
      judgementAdd.judgementAddDocuments.some(
        (doc: any) => doc.status === STATUS_PENDING,
      ) || status === STATUS_PENDING;
    const hasRejected =
      judgementAdd.judgementAddDocuments.some(
        (doc: any) => doc.status === STATUS_REJECTED,
      ) || status === PROCEDURE_STATUSES.TO_EDIT;
    const allValidated =
      judgementAdd.judgementAddDocuments.every(
        (doc: any) =>
          doc.status === STATUS_VALIDATED ||
          isEmpty(doc.status) ||
          doc.status === PROCEDURE_STATUSES.MODIFIED,
      ) &&
      (status === STATUS_VALIDATED || status === PROCEDURE_STATUSES.MODIFIED);
    if (hasPending) return JUDICIARY_COLOR_STATUS.PENDING;
    if (hasRejected) return JUDICIARY_COLOR_STATUS.REJECTED;
    if (allValidated) return JUDICIARY_COLOR_STATUS.VALIDATED;
  }

  if (stepType === 'expertReport' && !isEmpty(expertReport)) {
    const expertiseColor = getExpertiseStatusColor([
      ...get(expertReport, 'victimExpertises', []),
      ...get(expertReport, 'vehiculeExpertises', []),
    ]);
    return expertiseColor;
  }
  if (stepType === 'judgementOnMerits' && !isEmpty(judgementOnMerits)) {
    return getJudiciaryLabelColor(status);
  }
  return null;
};
export const getExpertiseStatusColor = (expertises: any[]) => {
  if (
    expertises.every(
      (expertise: any) =>
        expertise.status === STATUS_VALIDATED ||
        expertise.status === STATUS_CONTESTED ||
        expertise.status === MISSION_POSTPONE_STATUS.PENDING_APPRECIATION,
    )
  ) {
    return JUDICIARY_COLOR_STATUS.VALIDATED;
  }

  return expertises.some(
    (expertise: any) => expertise.status === STATUS_PENDING,
  )
    ? JUDICIARY_COLOR_STATUS.PENDING
    : expertises.some((expertise: any) => expertise.status === STATUS_REJECTED)
    ? JUDICIARY_COLOR_STATUS.REJECTED
    : undefined;
};
export const isJudiciaryStepDefined = (judiciaryStep: any) => {
  return (
    judiciaryStep.status === PROCEDURE_STATUSES.PENDING ||
    judiciaryStep.status === PROCEDURE_STATUSES.VALIDATED ||
    judiciaryStep.status === PROCEDURE_STATUSES.MODIFIED ||
    judiciaryStep.status === PROCEDURE_STATUSES.TO_EDIT
  );
};
export const showExpertReport = (judiciaryStep: any) => {
  return !isEmpty(get(judiciaryStep, 'judgementAddDocuments', []));
};
export const ShowExpertExpertise = (
  expertExpertise: any,
  judiciaryProcessId: any,
  procedureStep: any,
) => {
  return (
    !isEmpty(expertExpertise) &&
    !isEmpty(
      expertExpertise.filter(
        (el: any) =>
          el?.mission?.judiciaryProcessId === judiciaryProcessId &&
          el?.mission?.step === procedureStep,
      ),
    )
  );
};
export const ExtractExpertExpertise = (
  expertExpertise: any,
  judiciaryProcessId: any,
  procedureStep: any,
) => {
  if (
    !isEmpty(expertExpertise) &&
    !isEmpty(
      expertExpertise.filter(
        (el: any) =>
          el?.mission?.judiciaryProcessId === judiciaryProcessId &&
          el?.mission?.step === procedureStep,
      ),
    )
  ) {
    return expertExpertise.filter(
      (el: any) =>
        el?.mission?.judiciaryProcessId === judiciaryProcessId &&
        el?.mission?.step === procedureStep,
    );
  } else {
    return [];
  }
};
export const showJudgementOnMerits = (
  judiciaryStep: any,
  judgementAddClone: IJudgementAdd,
) => {
  return (
    isJudiciaryStepDefined(judiciaryStep) ||
    !isEmpty(get(judgementAddClone, 'judgementAdd', []))
  );
};
export const isDeceasedVictimsOnly = (victims: any) =>
  !victims.find((el: any) => el.injuryPostKey !== INJURY_POST_KEY.DECEASED);

export const getJudgementAddDocuments = (
  judgementAddDocuments: JudgementADDDocumentType[],
) =>
  judgementAddDocuments.filter((jadd: any) => jadd.status !== STATUS_REJECTED);

export const resolveJudgementAddDocs = (jaddDocs: any, jomArr: any = []) => {
  const civjsJomArr = jomArr.map((el: any) => get(el, 'jom.civjs', [])).flat();
  return jaddDocs.filter(
    (jaddDoc: any) =>
      !civjsJomArr
        .map((el: any) => el.judgementAddDocumentId)
        .includes(get(jaddDoc, 'id')),
  );
};

export const getJomVictims = (jomArr: any) => {
  const civjs = jomArr.map((el: any) => get(el, 'jom.civjs', [])).flat();
  return getCivjVictims(civjs);
};
export const getCivjVictims = (civjs: any) => {
  return civjs
    .map((el: any) =>
      [
        ...get(el, 'civjacce', []).map((exp: any) =>
          get(exp, 'victim.id', exp?.victimId),
        ),
        ...get(el, 'civjmede', []).map((exp: any) =>
          get(exp, 'victim.id', exp?.victimId),
        ),
      ].flat(),
    )
    .flat();
};
export const getAssignedLawyer = (
  isCivilProceeding: boolean = false,
  currentJudiciaryProcess: any,
  codeLawyer: any,
) => {
  return isCivilProceeding
    ? get(currentJudiciaryProcess, 'assignedLawyer', '')
    : codeLawyer;
};

export const getJomVehicles = (jomArr: any) => {
  return jomArr
    .map((el: any) => get(el, 'jom.civjs', []))
    .flat()
    .map((el: any) =>
      [
        ...get(el, 'civjmece', []).map((exp: any) => get(exp, 'vehicule.id')),
      ].flat(),
    )
    .flat();
};

export const resolveVehicles = (
  civilArr: any,
  jomArr: any,
  allVehicles: any,
  isCivil: boolean | undefined,
) => {
  if (isCivil) return difference([...civilArr], [...jomArr]);
  return difference([...allVehicles], [...jomArr]);
};

export const getDeceasedJomVictims = (jomArr: any, judgementAdd: any) => {
  return uniq(
    jomArr
      .map((el: any) => {
        const jomId = get(el, 'judgementOnMerits.jom.id');
        const beneficiaries = getBeneficiaries(judgementAdd, jomId);
        return beneficiaries
          .filter((el: any) => get(el, 'judgementOnMeritsId') === jomId)
          .map((el: any) => get(el, 'beneficiary.victimId'));
      })
      .flat(),
  );
};

export const constructLabelsWithIds = (civilProceeding: any) => {
  return orderBy(civilProceeding, ['procedureNatureId'], ['desc']).map(
    (
      el: {
        procedureNatureId: number;
        id: number;
        courtNumber: string;
      },
      index,
    ) => {
      return {
        id: el.id,
        label: formatLabel(
          get(
            ListProceeding.filter(
              element => element.id === el.procedureNatureId,
            ),
            '[0].title',
          ),
          el.courtNumber,
          el.procedureNatureId === JUDICIARY_PROCESS_CORRECTIONAL_ID
            ? false
            : true,
        ),
      };
    },
  );
};
export const constructLabels = (civilProceeding: any) => {
  return orderBy(civilProceeding, ['procedureNatureId'], ['desc']).map(
    (
      el: {
        procedureNatureId: number;
        id: number;
        courtNumber: string;
      },
      index,
    ) =>
      formatLabel(
        get(
          ListProceeding.filter(element => element.id === el.procedureNatureId),
          '[0].title',
        ),
        el.courtNumber,
        el.procedureNatureId === JUDICIARY_PROCESS_CORRECTIONAL_ID
          ? false
          : true,
      ),
  );
};

export const isCivilProceedingCheck = (
  civilProceeding: any,
  judiciaryProcessId: any,
) => {
  return (
    get(
      civilProceeding.find(
        (element: { id: number }) => element.id === judiciaryProcessId,
      ),
      'procedureNatureId',
    ) === JUDICIARY_PROCESS_CIVILE_ID ||
    get(
      civilProceeding.find(
        (element: { id: number }) => element.id === judiciaryProcessId,
      ),
      'procedureNatureId',
    ) === JUDICIARY_PROCESS_COMMERCIALE_ID
  );
};

export const constructAssignedLawyer = (
  claimRecap: ClaimRecapType,
  civilProceeding: any,
  judiciaryProcessId: any,
) => {
  const info = civilProceeding.find(
    (civilPro: any) => civilPro.id === judiciaryProcessId,
  );
  return get(claimRecap, ['claim', 'assignment'], [])
    .filter(
      (item: any) =>
        item.judiciaryProcessId === get(info, 'id') && item.isDeleted === false,
    )
    .map((el: any) => el.assignedLawyer);
};
export const checkIsAssignedLawyer = (
  assignedLawyers: any,
  sagis_code: any,
) => {
  return assignedLawyers && sagis_code && assignedLawyers.includes(sagis_code);
};

export const isCivilProceedingCommercialCheck = (
  civilProceeding: any,
  judiciaryProcessId: any,
) => {
  if (
    get(
      civilProceeding.find(
        (element: { id: number }) => element.id === judiciaryProcessId,
      ),
      'procedureNatureId',
    ) === JUDICIARY_PROCESS_CIVILE_ID ||
    get(
      civilProceeding.find(
        (element: { id: number }) => element.id === judiciaryProcessId,
      ),
      'procedureNatureId',
    ) === JUDICIARY_PROCESS_COMMERCIALE_ID
  ) {
    return true;
  } else {
    return false;
  }
};
export const resolveDisplayJomForm = (judgementOnMeritsMultiple: any) => {
  const foundJom = get(judgementOnMeritsMultiple, 'judgementOnMerits', []).find(
    (el: any) => el.isFirst === null,
  );
  if (foundJom === undefined) return true;
  return false;
};

export const proceedingLabels = (
  type: string,
  judgementAdd: any,
  props: any,
  isCivilProceedingCommercial: any,
  judgementOnMerits: any,
  judgementOnMeritsMultiple: any,
  jomLabels: any,
  currentJomMultiple: any,
  appealOnMeritsMultiple: any,
  jomAppealLabels: any,
) => {
  return isCivilProceedingCommercial
    ? type === PROCEDURE_TYPES.FIRST_INSTANCE
      ? [
          ...[
            messages.request,
            ...getSteps(
              type,
              showExpertReport(judgementAdd),
              ShowExpertExpertise(
                props.expertExpertise,
                props.judiciaryProcessId,
                1,
              ),
            ),
          ].map(label =>
            translate(label, props, {
              jomId: !isNil(
                get(
                  judgementOnMerits,
                  'jom.judgementDate',
                  get(judgementOnMeritsMultiple, '[0].jom.judgementDate', null),
                ),
              )
                ? props.intl.formatDate(
                    get(
                      judgementOnMerits,
                      'jom.judgementDate',
                      get(judgementOnMeritsMultiple, '[0].jom.judgementDate'),
                    ),
                    {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                    },
                  )
                : '',
            }),
          ),
          ...judgementOnMeritsMultiple
            .sort(
              (a: any, b: any) =>
                new Date(get(a, 'createdAt')).valueOf() -
                new Date(get(b, 'createdAt')).valueOf(),
            )
            .map((el: any, index: number) =>
              translate(messages.judgementOnMeritsLabelTitle, props, {
                jomId: !isNil(get(el, 'jom.judgementDate', null))
                  ? props.intl.formatDate(get(el, 'jom.judgementDate'), {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                    })
                  : '',
              }),
            ),
          ...jomLabels.map((label: string, index: number) =>
            translate(label, props, { jomId: index + 1 }),
          ),
        ]
      : [
          ...getSteps(
            type,
            showExpertReport(judgementAdd),
            ShowExpertExpertise(
              props.expertExpertise,
              props.judiciaryProcessId,
              2,
            ),
          ).map(label =>
            translate(label, props, {
              jomId:
                !isEmpty(currentJomMultiple) &&
                !isNil(get(currentJomMultiple, 'jom.judgementDate', null)) &&
                get(currentJomMultiple, 'procedureType') ===
                  PROCEDURE_TYPES.APPEAL
                  ? props.intl.formatDate(
                      get(currentJomMultiple, 'jom.judgementDate'),
                      {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                      },
                    )
                  : '',
            }),
          ),
          ...appealOnMeritsMultiple
            .sort(
              (a: any, b: any) =>
                new Date(get(a, 'createdAt')).valueOf() -
                new Date(get(b, 'createdAt')).valueOf(),
            )
            .map((el: any, index: number) =>
              translate(messages.appealOnMeritsLabelTitle, props, {
                appealId: !isNil(get(el, 'jom.judgementDate', null))
                  ? props.intl.formatDate(get(el, 'jom.judgementDate'), {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                    })
                  : '',
              }),
            ),
          ...jomAppealLabels.map((label: string, index: number) =>
            translate(label, props, { appealId: index + 1 }),
          ),
        ]
    : type === PROCEDURE_TYPES.FIRST_INSTANCE
    ? [
        ...getSteps(
          type,
          showExpertReport(judgementAdd),
          ShowExpertExpertise(
            props.expertExpertise,
            props.judiciaryProcessId,
            1,
          ),
        ).map(label =>
          translate(label, props, {
            jomId: !isNil(
              get(
                judgementOnMerits,
                'jom.judgementDate',
                get(judgementOnMeritsMultiple, '[0].jom.judgementDate', null),
              ),
            )
              ? props.intl.formatDate(
                  get(
                    judgementOnMerits,
                    'jom.judgementDate',
                    get(judgementOnMeritsMultiple, '[0].jom.judgementDate'),
                  ),
                  {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                  },
                )
              : '',
          }),
        ),
        ...judgementOnMeritsMultiple
          .sort(
            (a: any, b: any) =>
              new Date(get(a, 'createdAt')).valueOf() -
              new Date(get(b, 'createdAt')).valueOf(),
          )
          .map((el: any, index: number) =>
            translate(messages.judgementOnMeritsLabelTitle, props, {
              jomId: !isNil(get(el, 'jom.judgementDate', null))
                ? props.intl.formatDate(get(el, 'jom.judgementDate'), {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                  })
                : '',
            }),
          ),
        ...jomLabels.map((label: string, index: number) =>
          translate(label, props, { jomId: index + 1 }),
        ),
      ]
    : [
        ...getSteps(
          type,
          showExpertReport(judgementAdd),
          ShowExpertExpertise(
            props.expertExpertise,
            props.judiciaryProcessId,
            2,
          ),
        ).map(label =>
          translate(label, props, {
            jomId:
              !isEmpty(currentJomMultiple) &&
              !isNil(get(currentJomMultiple, 'jom.judgementDate', null)) &&
              get(currentJomMultiple, 'procedureType') ===
                PROCEDURE_TYPES.APPEAL
                ? props.intl.formatDate(
                    get(currentJomMultiple, 'jom.judgementDate'),
                    {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                    },
                  )
                : '',
          }),
        ),
        ...appealOnMeritsMultiple
          .sort(
            (a: any, b: any) =>
              new Date(get(a, 'createdAt')).valueOf() -
              new Date(get(b, 'createdAt')).valueOf(),
          )
          .map((el: any, index: number) =>
            translate(messages.appealOnMeritsLabelTitle, props, {
              appealId: !isNil(get(el, 'jom.judgementDate', null))
                ? props.intl.formatDate(get(el, 'jom.judgementDate'), {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                  })
                : '',
            }),
          ),
        ...jomAppealLabels.map((label: string, index: number) =>
          translate(label, props, { appealId: index + 1 }),
        ),
      ];
};

export const formatInvoiceFeeNoteInfos = (invoiceInfos: any) => {
  const filtredList = invoiceInfos.filter(
    (el: any) =>
      !isNil(get(el, 'invoiceNumber', null)) ||
      !isNil(get(el, 'invoiceDate', null)) ||
      !isNil(get(el, 'receptionDate', null)) ||
      !isNil(get(el, 'url', null)) ||
      !isNil(get(el, 'firmReference', null)),
  );
  return !isEmpty(filtredList)
    ? filtredList.map((el: any) => {
        if (
          isNil(get(el, 'invoiceNumber', null)) &&
          isNil(get(el, 'invoiceDate', null)) &&
          isNil(get(el, 'receptionDate', null)) &&
          !isNil(get(el, 'firmReference', null))
        ) {
          return {
            firmReference: get(el, 'firmReference', null),
            codeIntervenant: get(el, 'codeIntervenant', null),
          };
        } else {
          return {
            ...el,
            invoiceDate:
              el.invoiceDate === 'Invalid date' ? null : el.invoiceDate,
            receptionDate:
              el.receptionDate == 'Invalid date' ? null : el.receptionDate,
          };
        }
      })
    : filtredList;
};

export const isPendingApprovalClaim = (claimRecap: any) => {
  return (
    get(claimRecap, 'claim.status', null) === CLAIM_STATUS.PENDING_APPROVAL
  );
};

export const getExpertExpertise = (
  expertExpertise: any,
  judiciaryProcessId: any,
  step: any,
) => {
  return (
    !isEmpty(expertExpertise) &&
    expertExpertise.filter(
      (el: any) =>
        el?.mission?.judiciaryProcessId === judiciaryProcessId &&
        el?.mission?.step === step,
    )
  );
};

export const formatRenderJudgementOnMerits = (
  el: any,
  judgementOnMeritsMultiple: any,
) => {
  return {
    judgementOnMerits: { ...el },
    beneficiaries: [...get(judgementOnMeritsMultiple, 'beneficiaries', [])],
  };
};

export const sortJudgement = (judgements: any) => {
  return judgements.sort(
    (a: any, b: any) =>
      new Date(get(a, 'createdAt')).valueOf() -
      new Date(get(b, 'createdAt')).valueOf(),
  );
};

export const getResponsabilityShareJom = (judgementOnMerits: any) => {
  return (
    get(
      getCivilJudgement(judgementOnMerits as any),
      '[0].responsibilityShare',
      '',
    ) ||
    get(
      judgementOnMerits,
      'judgementOnMerits.jom.civjvm.[0].responsibilityShare',
      null,
    ) ||
    get(
      judgementOnMerits,
      'beneficiaries.[0].beneficiary.victim.responsibilityShare',
      null,
    )
  );
};

export const getAssignementLawyer = (
  claimAssignment: any,
  judiciaryProcessId: any,
) => {
  const assignementInfo = claimAssignment.find(
    (el: any) =>
      el.assignedLawyer === currentIntervenantStorage.get()?.sagis_code &&
      el.judiciaryProcessId === judiciaryProcessId &&
      el.isDeleted === false,
  );
  return assignementInfo;
};

export const formatVictimsToDahirAmount = (victims: any) => {
  return {
    injured: [
      ...without(
        uniq(
          victims
            .filter((v: any) => v['injuryPostKey'] !== '2')
            .map((v: any) => v.victimKey),
        ),
        null,
        '',
      ),
    ],
    deceased: [
      ...without(
        uniq(
          victims
            .filter((v: any) => v['injuryPostKey'] === '2')
            .map((v: any) => v.victimKey),
        ),
        null,
        '',
      ),
    ],
  };
};

export const formatNextOfKins = (
  beneficiaries: BeneficiaryType[],
  sharedResponsability: string,
) => {
  return beneficiaries.map((el: any) => {
    return {
      victimKey: get(el, 'victimKey'),
      ayantDroit: {
        fullName: `${removeWhiteSpaceFromString(
          el.firstName,
        )} ${removeWhiteSpaceFromString(el.lastName)}`,
        qualityCode: Number(el.beneficiaryType),
        age: Number(el.age),
        birthDate: el?.dateOfBirth,
        funeralFees: featureService.hasFeature(FEATURES.NEW_LAW_DAHIR)
          ? el.funeralFees
          : !isNil(el.funeralFees) &&
            !!Number(sharedResponsability) &&
            Number(sharedResponsability) > 0
          ? parseFloat(
              String(el.funeralFees / (Number(sharedResponsability) / 100)),
            ).toFixed(2)
          : el.funeralFees,
        medicalFees: featureService.hasFeature(FEATURES.NEW_LAW_DAHIR)
          ? el.medicalFees
          : !isNil(el.medicalFees) &&
            !!Number(sharedResponsability) &&
            Number(sharedResponsability) > 0
          ? parseFloat(
              String(el.medicalFees / (Number(sharedResponsability) / 100)),
            ).toFixed(2)
          : el.medicalFees,
        nextOfKinKey: !isNil(get(el, 'nextOfKinKey'))
          ? get(el, 'nextOfKinKey')
          : undefined,
        handicapIndicator: el.handicapIndicator ? 'Y' : 'N',
        dependentIndicator: el.dependentIndicator ? 'Y' : 'N',
      },
    };
  });
};
export const getIntervenantInfo = () => {
  return {
    code:
      currentIntervenantStorage.get() &&
      currentIntervenantStorage.get().sagis_code,
    name:
      currentIntervenantStorage.get() && currentIntervenantStorage.get().name,
  };
};
export const getCurrentTabJudiciaryProcess = (
  JudiciaryProcessId: any,
  civilProceeding: any,
) => {
  return JudiciaryProcessId
    ? _.orderBy(civilProceeding, ['procedureNatureId'], ['desc'])
        .map((el: { id: number }) => el.id)
        .indexOf(Number(JudiciaryProcessId as any) as any)
    : 0;
};
export const containsCompletedTransaction = (
  transactions: TransactionType[],
) => {
  return (
    !isEmpty(transactions) &&
    !isEmpty(
      transactions?.filter(
        (el: TransactionType) => el.state === TRANSACTION_STATE.DONE,
      ),
    )
  );
};

export const renderGlobalTabs = (props: any, containsTransaction: boolean) => {
  const commonTabsLabels = [
    messages.recapTabTitle,
    messages.judiciaryProcessTabTitle,
    messages.expertise,
  ];

  const labels = (containsTransaction &&
  featureService.hasFeature(FEATURES.TRANSACTION)
    ? [...commonTabsLabels, messages.transactionTabTitle]
    : commonTabsLabels
  ).map(label => translate(label, props));
  return labels;
};

export const getContractorFeesByIntervenant = (
  sagisInfo: any,
  sagis_code: string,
) => {
  return sagisInfo?.contractorFees?.filter((el: any) => {
    return el.contractorCode === sagis_code;
  });
};

export const getLastGetInstruction = (instructions: InstructionType[]) => {
  return last(
    instructions &&
      instructions.sort(
        (objA: any, objB: any) =>
          Number(new Date(objA?.createdAt)) - Number(new Date(objB?.createdAt)),
      ),
  );
};

export const isLastInstructionLawyerMismatch = (
  instructions: InstructionType[],
) => {
  const lastInstruction = getLastGetInstruction(instructions);
  const lawyerCode = get(lastInstruction, 'lawyerCode');
  const currentSagisCode = currentIntervenantStorage.get()?.sagis_code;

  return lawyerCode !== currentSagisCode;
};
